import type { RouteRecordRaw } from 'vue-router'
import { routes } from '@app/router/routes'

// Authenticated listing-request routes, mounted under the AppLayout shell.
// - LISTINGS       : paginated list of the seller's requests
// - LISTING_DETAIL : one request's lifecycle detail (must sit AFTER `/new`
//   is registered as its own static path, but vue-router matches static
//   `/listings/new` before the `:id` param regardless of order here).
//
// The create wizard is NOT here — it runs full-screen without the app sidebar,
// so it is published separately as `listingRequestWizardRoutes` below and the
// router mounts it under WizardLayout.
export const listingRequestRoutes: RouteRecordRaw[] = [
  {
    path: routes.LISTINGS,
    name: 'LISTINGS',
    component: () => import('./ListingsListPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Sell Request',
      subtitle: 'Track the status of your property sell requests.',
    },
  },
  {
    // Fixed 3-segment path — the signer lands here after UAE Pass; the target
    // agreement/signatory is read from sessionStorage, not the URL. Registered
    // before LISTING_DETAIL so `/listings/:id` never shadows it.
    path: routes.LISTING_AGREEMENT_SIGN_RETURN,
    name: 'LISTING_AGREEMENT_SIGN_RETURN',
    component: () => import('./AgreementSignReturnPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Finalising signature',
      subtitle: 'Completing your agreement signature.',
    },
  },
  {
    path: routes.LISTING_DETAIL,
    name: 'LISTING_DETAIL',
    component: () => import('./ListingRequestDetailPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'Request Details',
      subtitle: 'Lifecycle, documents, and activity for this request.',
    },
  },
]

// The create wizard, mounted under WizardLayout instead of the app shell: it owns
// the whole viewport below the brand bar (progress rail + step column + action
// bar) and deliberately drops the sidebar so there is no half-finished listing to
// wander away from. Still `requiresAuth` — the router guard reads `meta` off every
// matched record, so the gate is unchanged by the shell swap.
//
// `title` / `subtitle` are carried for parity with the other routes; WizardLayout
// renders neither, since the wizard prints its own per-step heading.
export const listingRequestWizardRoutes: RouteRecordRaw[] = [
  {
    path: routes.LISTING_NEW,
    name: 'LISTING_REQUEST_NEW',
    component: () => import('./ListingRequestWizardPage.vue'),
    meta: {
      requiresAuth: true,
      title: 'New Sell Request',
      subtitle: 'List your property in a few guided steps.',
    },
  },
]
